import React from 'react';
import FlexGroup from './FlexGroup';
import styled from '@emotion/styled';
import Badge from './Badge';
import Icon, { IconType } from './Icon';
import { useTectonTheme } from '@lithos';
import { LithosComponent } from './LithosComponent';

interface TabItem {
  label: string;
  icon?: IconType;
  badgeCount?: number;
  disabled?: boolean;
  route?: string;
}

export interface TabsProps {
  items: TabItem[];
  selectedTab: string;
  setSelectedTab: (tab: any) => void;
}

const TabWrapper = styled.div<{ active: boolean; disabled: boolean }>`
  border-bottom: ${({ theme, active }) =>
    `1px solid ${active ? theme.v1.colors.border.primary : theme.v1.colors.background.transparent}`};
  color: ${({ theme, disabled, active }) => {
    if (active) {
      return theme.v1.colors.text.title;
    }
    return disabled ? theme.v1.colors.text.disabledText : theme.v1.colors.text.text;
  }};
  font-weight: ${({ theme, active }) => (active ? theme.v1.font.weight.medium : theme.v1.font.weight.regular)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  padding: ${({ theme }) => `${theme.v1.size['2']}px ${theme.v1.size['1.5']}px`};

  &:hover {
    border-bottom: ${({ theme, active, disabled }) => {
      if (disabled) {
        return `1px solid ${theme.v1.colors.background.transparent}`;
      }
      return `1px solid ${active ? theme.v1.colors.border.primary : theme.v1.colors.background.disabled}`;
    }};
    color: ${({ theme, disabled }) => (disabled ? theme.v1.colors.text.disabledText : theme.v1.colors.text.title)};
  }
`;

const ItemWrapper = styled.div`
  overflow: hidden;
  white-space: nowrap;
`;

const Tabs: LithosComponent<TabsProps> = ({ items, selectedTab, setSelectedTab, testId = 'tabs-wrapper' }) => {
  const { theme } = useTectonTheme();

  return (
    <div data-testid={testId} style={{ width: '100%', overflow: 'auto' }}>
      <FlexGroup
        gapSize="4"
        alignItems="flex-end"
        css={{ borderBottom: `1px solid ${theme.v1.colors.border.default}` }}
        padding="0"
        data-testid={testId}
      >
        {items.map((tab) => {
          const showTab = tab && tab.badgeCount! > 0;
          return (
            <TabWrapper
              active={selectedTab === tab.label}
              disabled={tab.disabled ?? false}
              onClick={() => {
                if (tab.disabled) return;
                setSelectedTab(tab.label);
              }}
            >
              <FlexGroup
                alignItems="center"
                gapSize="1.5"
                padding="0"
                testId={`tab-${tab.label.toLowerCase().replace(' ', '-')}`}
                data-route={tab.route}
              >
                <ItemWrapper>
                  {tab.icon && <Icon size="s" icon={tab.icon} type="mono" />}
                  {tab.label}
                  {showTab && <Badge label={tab.badgeCount} size="s" />}
                </ItemWrapper>
              </FlexGroup>
            </TabWrapper>
          );
        })}
      </FlexGroup>
    </div>
  );
};

export default Tabs;
